<template>
  <div class="column content-box">
    <report-filter-selector
      :show-agents-filter="true"
      :show-inbox-filter="true"
      :show-rating-filter="true"
      :show-team-filter="isTeamsEnabled"
      :show-business-hours-switch="false"
      :show-thophy-icon="true"
      @filter-change="onFilterChange"
    />
    <woot-button
      v-if="isCsat"
      color-scheme="success"
      class-names="button--fixed-top mt-50 bg-primary"
      icon="arrow-download"
      @click="downloadReports"
    >
      {{ $t('CSAT_REPORTS.DOWNLOAD') }}
    </woot-button>
    <div class="csat-refresh-bar" v-if="isCsat">
      <woot-button
        color-scheme="primary"
        size="small"
        icon="arrow-clockwise"
        :is-loading="isRefreshing"
        :is-disabled="isRefreshing || isCooldownActive"
        @click="refreshMetrics"
      >
        <template v-if="isRefreshing">{{ $t('CSAT_REPORTS.REFRESHING') }}</template>
        <template v-else-if="isCooldownActive">{{ cooldownLabel }}</template>
        <template v-else>{{ $t('CSAT_REPORTS.REFRESH') }}</template>
      </woot-button>
      <span v-if="metricsCachedAt" class="csat-cached-info">
        {{ $t('CSAT_REPORTS.LAST_UPDATED') }}: {{ formattedCachedAt }}
      </span>
    </div>
    <div >
      <csat-metrics :filters="requestPayload" />
      <csat-table :page-index="pageIndex" @page-change="onPageNumberChange" />
      <resolved-filters-thophy-icon  />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import alertMixin from '../../../../../shared/mixins/alertMixin';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { generateFileName } from '../../../../helper/downloadHelper';
import CsatMetrics from './components/CsatMetrics';
import CsatTable from './components/CsatTable';
import ReportFilterSelector from './components/FilterSelector';
import ResolvedFiltersThophyIcon from './components/Filters/TrophyIcon.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    ReportFilterSelector,
    ResolvedFiltersThophyIcon,
  },
  mixins: [alertMixin],
  props: {
    deferFetch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageIndex: 1,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      is_problem_solved: null,
      isRankingCsatSelected: null,
      fetchUnlocked: true,
      cooldownSeconds: 0,
      cooldownTimer: null,
    };
  },
  created() {
    if (this.deferFetch) {
      this.fetchUnlocked = false;
    }
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'csat/getUIFlags',
      metricsCachedAt: 'csat/getMetricsCachedAt',
      cooldownRemaining: 'csat/getCooldownRemaining',
    }),
    isRefreshing() {
      return this.uiFlags.isRefreshing;
    },
    isCooldownActive() {
      return this.cooldownSeconds > 0;
    },
    cooldownLabel() {
      const min = Math.floor(this.cooldownSeconds / 60);
      const sec = this.cooldownSeconds % 60;
      const pad = sec < 10 ? '0' : '';
      return `${min}:${pad}${sec}`;
    },
    formattedCachedAt() {
      if (!this.metricsCachedAt) return '';
      const date = new Date(this.metricsCachedAt);
      return date.toLocaleString();
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
        is_problem_solved: this.is_problem_solved,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
    isCsat(){
      return this.$route.path.includes('csat');
    }
  },
  watch: {
    cooldownRemaining(newVal) {
      if (newVal > 0) this.startCooldownTimer(newVal);
    },
  },
  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        this.$store.dispatch('csat/checkCooldown', this.requestPayload);
        this.getResponses();
      } catch {
        this.showAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    refreshMetrics() {
      this.$store.dispatch('csat/refreshMetrics', this.requestPayload);
    },
    startCooldownTimer(seconds) {
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownSeconds = seconds;
      this.cooldownTimer = setInterval(() => {
        this.cooldownSeconds -= 1;
        if (this.cooldownSeconds <= 0) {
          this.cooldownSeconds = 0;
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },
    handleRatingFilterSelectCsatRanking(selectedRating){
      this.selectedRating = selectedRating;
      this.emitChange();
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex,
        ...this.requestPayload,
      });
    },
    downloadReports() {
      const type = 'csat';
      try {
        this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch (error) {
        this.showAlert(this.$t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'));
      }
    },
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      if (this.fetchUnlocked) {
        this.getResponses();
      }
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
      is_problem_solved,
    }) {
      // do not track filter change on inital load
      if (this.from !== 0 && this.to !== 0) {
        this.$track(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: 'csat',
        });
      }

      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;
      this.is_problem_solved = is_problem_solved?.id;

      if (this.fetchUnlocked) {
        this.getAllData();
      }
    },
    loadCsatData() {
      this.fetchUnlocked = true;
      this.getAllData();
    },
  },
};
</script>
<style scoped>
.csat-refresh-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.csat-cached-info {
  font-size: 12px;
  color: var(--s-600);
}
</style>
